import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
  useTheme
} from "@mui/material";
import { IconDownload, IconTrash } from "@tabler/icons";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import PageContainer from "src/components/container/PageContainer";
import useFileJobs from "src/hooks/useFileJobs";

const statusColors = {
  pending: "warning",
  processing: "info",
  completed: "success",
  failed: "error",
};

const FileJobs = () => {
  const theme = useTheme();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    pending: 0,
    processing: 0,
    completed: 0,
    failed: 0,
  });

  const {
    isConnected,
    jobUpdates,
    newJobs,
    fetchJobs,
    downloadFile,
    deleteJob,
    getJobStats,
  } = useFileJobs();

  // Fetch jobs on mount
  useEffect(() => {
    loadJobs();
    loadStats();
  }, []);

  // Update jobs when we get socket updates
  useEffect(() => {
    if (Object.keys(jobUpdates).length > 0) {
      setJobs((prevJobs) =>
        prevJobs.map((job) =>
          jobUpdates[job.jobId] ? { ...job, ...jobUpdates[job.jobId] } : job
        )
      );
      // Also update stats when jobs change
      loadStats();
    }
  }, [jobUpdates]);

  // Add new jobs when they are created
  useEffect(() => {
    if (newJobs.length > 0) {
      setJobs((prevJobs) => {
        // Filter out jobs that are already in the list to avoid duplicates
        const existingJobIds = new Set(prevJobs.map(job => job.jobId));
        const jobsToAdd = newJobs.filter(job => !existingJobIds.has(job.jobId));
        return [...jobsToAdd, ...prevJobs];
      });
      // Update stats when new jobs are added
      loadStats();
    }
  }, [newJobs]);

  const loadJobs = async () => {
    try {
      setLoading(true);
      const result = await fetchJobs({ page: 1, limit: 50 });
      if (result.success) {
        setJobs(result.data.results);
      }
    } catch (error) {
      console.error("Error loading jobs:", error);
      toast.error("Failed to load file jobs");
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const result = await getJobStats();
      if (result.success) {
        setStats(result.data);
      }
    } catch (error) {
      console.error("Error loading stats:", error);
    }
  };

  const handleDownload = async (job) => {
    try {
      await downloadFile(job.jobId);
      toast.success(`Downloading ${job.outputFileName || job.fileName}`);
    } catch (error) {
      toast.error("Failed to download file");
    }
  };

  const handleDelete = async (jobId) => {
    try {
      await deleteJob(jobId);
      setJobs((prev) => prev.filter((j) => j.jobId !== jobId));
      toast.success("Job deleted");
      loadStats();
    } catch (error) {
      toast.error("Failed to delete job");
    }
  };

  const formatDate = (date) => {
    try {
      return format(new Date(date), "MMM dd, yyyy HH:mm");
    } catch {
      return "-";
    }
  };

  const getProgressColor = (status) => {
    switch (status) {
      case "completed":
        return theme.palette.success.main;
      case "failed":
        return theme.palette.error.main;
      case "processing":
        return theme.palette.info.main;
      default:
        return theme.palette.warning.main;
    }
  };

  return (
    <PageContainer
      title="File Jobs | EarnPati"
      description="View and manage file processing jobs"
    >
      <Grid container spacing={3}>
        {/* Stats Cards */}
        <Grid item xs={12}>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Card sx={{ minWidth: 150 }}>
              <CardContent>
                <Typography color="textSecondary" variant="body2">
                  Pending
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {stats.pending}
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ minWidth: 150 }}>
              <CardContent>
                <Typography color="textSecondary" variant="body2">
                  Processing
                </Typography>
                <Typography variant="h4" color="info.main">
                  {stats.processing}
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ minWidth: 150 }}>
              <CardContent>
                <Typography color="textSecondary" variant="body2">
                  Completed
                </Typography>
                <Typography variant="h4" color="success.main">
                  {stats.completed}
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ minWidth: 150 }}>
              <CardContent>
                <Typography color="textSecondary" variant="body2">
                  Failed
                </Typography>
                <Typography variant="h4" color="error.main">
                  {stats.failed}
                </Typography>
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        {/* Connection Status */}
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 1,
              borderRadius: 1,
              bgcolor: isConnected
                ? theme.palette.success.light
                : theme.palette.error.light,
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: isConnected ? "success.main" : "error.main",
              }}
            />
            <Typography variant="body2">
              {isConnected
                ? "Real-time updates connected"
                : "Connecting to real-time updates..."}
            </Typography>
          </Box>
        </Grid>

        {/* Jobs List */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Processing Jobs
              </Typography>

              {loading ? (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  sx={{ height: 200 }}
                >
                  <CircularProgress />
                </Stack>
              ) : jobs.length === 0 ? (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  sx={{ height: 200 }}
                >
                  <Typography variant="body1" color="textSecondary">
                    No file jobs found
                  </Typography>
                </Stack>
              ) : (
                <Stack spacing={2}>
                  {jobs.map((job) => (
                    <JobItem
                      key={job.jobId}
                      job={job}
                      onDownload={() => handleDownload(job)}
                      onDelete={() => handleDelete(job.jobId)}
                      getProgressColor={getProgressColor}
                      formatDate={formatDate}
                    />
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </PageContainer>
  );
};

const JobItem = ({
  job,
  onDownload,
  onDelete,
  getProgressColor,
  formatDate,
}) => {
  const theme = useTheme();
  const isCompleted = job.status === "completed";
  const isFailed = job.status === "failed";
  const isProcessing = job.status === "processing";

  // Normalize displayed progress based on status
  const displayProgress = isCompleted ? 100 : isFailed ? 0 : (job.progress || 0);

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor:
          job.status === "processing"
            ? theme.palette.info.light
            : theme.palette.divider,
      }}
    >
      <CardContent>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Chip
                label={job.status}
                color={statusColors[job.status] || "default"}
                size="small"
              />
              <Typography variant="subtitle1" noWrap>
                {job.fileName}
              </Typography>
            </Stack>
            <Typography variant="caption" color="textSecondary">
              Type: {job.fileType}
            </Typography>
            <br />
            <Typography variant="caption" color="textSecondary">
              Created: {formatDate(job.createdAt)}
            </Typography>
          </Grid>

          <Grid item xs={12} md={5}>
            <Stack spacing={1}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2">{job.currentStep}</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {displayProgress}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={displayProgress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: theme.palette.grey[200],
                  "& .MuiLinearProgress-bar": {
                    bgcolor: getProgressColor(job.status),
                    borderRadius: 4,
                  },
                }}
              />
            </Stack>

            {isFailed && job.error && (
              <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                Error: {job.error}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} md={3}>
            <Stack
              direction="row"
              spacing={1}
              justifyContent={{ xs: "flex-start", md: "flex-end" }}
            >
              {isCompleted && (
                <IconButton
                  color="primary"
                  onClick={onDownload}
                  title="Download"
                  size="small"
                >
                  <IconDownload />
                </IconButton>
              )}
              <IconButton
                color="error"
                onClick={onDelete}
                title="Delete"
                size="small"
              >
                <IconTrash />
              </IconButton>
            </Stack>
          </Grid>
        </Grid>

        {job.status === "processing" && (
          <Box
            sx={{
              mt: 2,
              p: 1,
              bgcolor: theme.palette.info.light,
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <CircularProgress size={16} />
            <Typography variant="caption">
              Processing in background. You can leave this page and come back
              later.
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default FileJobs;
